import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Database, Play, Download, Trash2, Clock, Save,
  AlertTriangle, Check, RefreshCw, Loader2, ShieldCheck,
} from "lucide-react";
import axios from "axios";

/**
 * BackupsCentre — Owner Center page for the daily-backup service.
 *
 * The local portal backend (see `/app/backend/server.py`) runs a scheduled
 * snapshot every day at the configured UTC hour and stores it on disk +
 * indexes it in Mongo. This page:
 *
 *   • Lists the last N snapshots (date, size, resource counts, source)
 *   • Lets the Owner trigger a manual "Backup Now" using their current
 *     Bearer token so nothing needs pre-configuration.
 *   • Lets the Owner save a service token so the daily job runs
 *     unattended (the token itself is never returned by the server —
 *     the settings endpoint only reports whether one is configured).
 *   • Lets the Owner download or delete individual snapshots.
 *
 * Auth: the portal Bearer token comes from `localStorage.token` (set by
 * the AuthContext on login). All calls target the portal's own backend
 * via REACT_APP_BACKEND_URL so this UI never touches the prod API
 * directly for backup operations.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const backend = axios.create({ baseURL: BACKEND });

export default function BackupsCentre() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [runningNow, setRunningNow] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [tokenDraft, setTokenDraft] = useState("");
  const [siteDraft, setSiteDraft] = useState("");
  const [hourDraft, setHourDraft] = useState(15);

  const reload = async () => {
    setBusy(true); setMsg(null);
    try {
      const [b, s] = await Promise.all([
        backend.get("/api/backups"),
        backend.get("/api/backups/settings"),
      ]);
      setItems(Array.isArray(b.data) ? b.data : []);
      setSettings(s.data || {});
      setSiteDraft(s.data?.site_code || "");
      setHourDraft(s.data?.hour_utc ?? 15);
    } catch (ex) {
      setMsg({ type: "err", text: ex?.response?.data?.detail || ex?.message || "Failed to load." });
    } finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, []);

  const runNow = async () => {
    setRunningNow(true); setMsg(null);
    try {
      // Pull the caller's current portal token. AuthContext stores it as
      // `sl_token` (see /app/frontend/src/context/AuthContext.jsx).
      const token = localStorage.getItem("sl_token")
                 || localStorage.getItem("shiftlog_token")
                 || localStorage.getItem("token")
                 || localStorage.getItem("access_token")
                 || "";
      const site = siteDraft || settings?.site_code || null;
      const { data } = await backend.post("/api/backups/run", { token, site_code: site });
      setMsg({ type: "ok", text: `Snapshot ${data._id.slice(0, 8)} · ${(data.size_bytes/1024).toFixed(0)}kb · ${data.duration_seconds}s` });
      reload();
    } catch (ex) {
      setMsg({ type: "err", text: ex?.response?.data?.detail || ex?.message || "Backup failed." });
    } finally { setRunningNow(false); }
  };

  const saveSettings = async () => {
    setSavingSettings(true); setMsg(null);
    try {
      const payload = { site_code: siteDraft || null, hour_utc: Number(hourDraft) || 15 };
      if (tokenDraft) payload.service_token = tokenDraft;
      const { data } = await backend.put("/api/backups/settings", payload);
      setMsg({ type: "ok", text: `Auto-backup scheduled at ${String(data.hour_utc).padStart(2, "0")}:00 UTC` });
      setTokenDraft("");
      reload();
    } catch (ex) {
      setMsg({ type: "err", text: ex?.response?.data?.detail || ex?.message || "Failed to save settings." });
    } finally { setSavingSettings(false); }
  };

  const download = (bid) => {
    // Use a plain <a> download so the browser handles the JSON stream.
    window.open(`${BACKEND}/api/backups/${bid}/download`, "_blank", "noopener");
  };

  const remove = async (bid) => {
    if (!window.confirm("Delete this snapshot permanently?")) return;
    setBusy(true);
    try {
      await backend.delete(`/api/backups/${bid}`);
      setMsg({ type: "ok", text: "Snapshot removed." });
      reload();
    } catch (ex) {
      setMsg({ type: "err", text: ex?.response?.data?.detail || ex?.message || "Delete failed." });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" data-testid="backups-page">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3">
        <button className="btn btn-sm btn-ghost" onClick={() => nav(-1)} data-testid="backups-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button className="btn btn-sm btn-ghost" onClick={reload} data-testid="backups-refresh">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="px-5 pb-10 space-y-6">
        {/* Hero */}
        <section className="panel-heavy p-6 md:p-8" data-testid="backups-hero">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted flex items-center gap-2">
                <Database className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} /> Site Backups
              </div>
              <h1 className="font-display font-black text-4xl sm:text-5xl leading-tight mt-2 tracking-tight">
                Daily Snapshots
              </h1>
              <p className="text-muted mt-2 text-sm max-w-xl">
                A full JSON pack of every asset, inspection, PPF, breakdown, handover, hazard, template & site config is captured once a day. Download any snapshot to keep an off-site copy, or restore via the mobile-app admin console.
              </p>
            </div>
            <button
              className="btn btn-primary btn-lg"
              onClick={runNow}
              disabled={runningNow}
              data-testid="backups-run-now"
            >
              {runningNow ? <><Loader2 className="w-4 h-4 animate-spin" /> Running…</> : <><Play className="w-4 h-4" /> Backup now</>}
            </button>
          </div>

          {msg && (
            <div
              className={`mt-4 pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs justify-start`}
              data-testid={`backups-msg-${msg.type}`}
            >
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              {msg.text}
            </div>
          )}
        </section>

        {/* Schedule / service token */}
        <section className="panel p-5" data-testid="backups-settings">
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted flex items-center gap-2 mb-3">
            <Clock className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} /> Auto-Backup Schedule
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Daily hour (UTC 0-23)</div>
              <input
                type="number" min="0" max="23"
                className="input"
                value={hourDraft}
                onChange={(e) => setHourDraft(e.target.value)}
                data-testid="backups-hour"
              />
              <div className="text-[10px] text-muted mt-1">15:00 UTC ≈ 02:00 AEST (Sydney)</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Site code</div>
              <input
                type="text"
                className="input"
                placeholder="WHC-MC001"
                value={siteDraft}
                onChange={(e) => setSiteDraft(e.target.value)}
                data-testid="backups-site"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3" /> Service token
                {settings?.auto_backup_enabled && (
                  <span className="ml-1 text-[9px] font-black tracking-widest" style={{ color: "var(--primary)" }}>
                    CONFIGURED
                  </span>
                )}
              </div>
              <input
                type="password"
                className="input"
                placeholder={settings?.auto_backup_enabled ? "•••••• (leave blank to keep)" : "Paste a service Bearer token"}
                value={tokenDraft}
                onChange={(e) => setTokenDraft(e.target.value)}
                data-testid="backups-token"
              />
              <div className="text-[10px] text-muted mt-1">
                Used by the daily job only — never returned by the API.
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              className="btn btn-primary btn-sm"
              onClick={saveSettings}
              disabled={savingSettings}
              data-testid="backups-save-settings"
            >
              {savingSettings ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : <><Save className="w-3.5 h-3.5" /> Save schedule</>}
            </button>
          </div>
        </section>

        {/* Snapshot history */}
        <section className="panel p-5" data-testid="backups-list">
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted flex items-center gap-2 mb-3">
            <Database className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
            Snapshots · {items.length}
          </div>
          {items.length === 0 && !busy && (
            <div className="text-sm text-muted py-6 text-center">
              No backups yet. Click <span className="font-bold" style={{ color: "var(--primary)" }}>Backup now</span> to make the first one.
            </div>
          )}
          <div className="space-y-2">
            {items.map((b) => (
              <div
                key={b._id}
                className="rounded-lg px-4 py-3 flex items-center gap-3 flex-wrap"
                style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
                data-testid={`backup-row-${b._id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{b.file}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted mt-0.5">
                    {b.source} · {new Date(b.started_at).toLocaleString()} · {(b.size_bytes/1024).toFixed(0)}kb · {b.duration_seconds}s
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {Object.entries(b.resource_counts || {}).map(([k, v]) => (
                      <span
                        key={k}
                        className="px-2 py-0.5 rounded text-[10px] font-black tracking-widest"
                        style={{ background: "rgba(245,130,32,0.12)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}
                      >
                        {k}·{v}
                      </span>
                    ))}
                    {Object.entries(b.errors || {}).map(([k]) => (
                      <span key={"e"+k} className="px-2 py-0.5 rounded text-[10px] font-black tracking-widest"
                        style={{ background: "rgba(220,40,40,0.12)", color: "#f87171", border: "1px solid rgba(220,40,40,0.3)" }}
                        title={b.errors[k]}
                      >{k}·err</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => download(b._id)}
                    data-testid={`backup-download-${b._id}`}
                    title="Download JSON"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => remove(b._id)}
                    data-testid={`backup-delete-${b._id}`}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
